import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { mantineTheme } from '@bcc-life/sandbox/assets';
import {
  BCCLIFE_CONFIG,
  sandboxAxiosConfig,
  sandboxConsoleErrorConfig,
  sandboxI18nConfig,
  sandboxYupConfig,
  sandboxDayjsConfig,
} from '@bcc-life/sandbox/utils';

import AppSuspense from '~/AppSuspense';
import { kzTranslations } from '~/consts/i18n/kz';
import { ruTranslations } from '~/consts/i18n/ru';
import { PageMetaContextProvider } from '~/context/PageMetaContext';
import { UserContextProvider } from '~/context/UserContext';
import '~/assets/globals.scss';

import reportWebVitals from './utils/reportWebVitals';

sandboxI18nConfig({
  thirdParty: initReactI18next,
  translations: { kz: kzTranslations, ru: ruTranslations },
});
sandboxAxiosConfig({ type: 'CABINET', url: BCCLIFE_CONFIG.API_URL || '' });
sandboxYupConfig();
sandboxDayjsConfig();

if (process.env.NODE_ENV === 'development') {
  sandboxConsoleErrorConfig();
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

const root = document.getElementById('root');

if (root) {
  document.documentElement.lang = localStorage.getItem('language') || 'ru';
  createRoot(root).render(
    <StrictMode>
      <MantineProvider theme={mantineTheme}>
        <Notifications position="top-right" zIndex={2000} />

        <ModalsProvider>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <PageMetaContextProvider>
                <UserContextProvider>
                  <BrowserRouter
                    future={{
                      v7_relativeSplatPath: false,
                      v7_startTransition: true,
                    }}
                  >
                    <AppSuspense />
                  </BrowserRouter>
                </UserContextProvider>
              </PageMetaContextProvider>
            </HelmetProvider>

            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ModalsProvider>
      </MantineProvider>
    </StrictMode>,
  );
}

reportWebVitals();
